import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SocialIcon } from 'react-social-icons';
import ContactForm from '../components/ContactForm';
import './FAQPage.css';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    id: 1,
    category: "Koszty i Płatności",
    question: "Ile kosztują zajęcia w Sigma Study?",
    answer: "Ceny zaczynają się od 100 zł za godzinę za Matematykę Podstawową. Oferujemy również pakiety zajęć w atrakcyjnych cenach."
  },
  {
    id: 2,
    category: "Koszty i Płatności",
    question: "Czy oferujecie zniżki dla uczniów?",
    answer: "Tak! Oferujemy zniżki dla uczniów uczęszczających na więcej niż jeden kurs oraz zniżki rodzinne. Skontaktuj się z nami, aby omówić indywidualną ofertę cenową."
  },
  {
    id: 3,
    category: "Kursy i Programy",
    question: "Jakie kursy matematyki oferujecie?",
    answer: "Oferujemy 8 różnych kursów: Matura Podstawowa, Matura Rozszerzona, Egzamin Ósmoklasisty, Konkursy i Olimpiady, Egzaminy Zagraniczne (SAT, ACT, A-levels), Matematyka Podstawowa, Indywidualne Potrzeby oraz Nadrabianie Zaległości."
  },
  {
    id: 4,
    category: "Kursy i Programy",
    question: "Czy przygotowujecie do egzaminów zagranicznych?",
    answer: "Tak! Specjalizujemy się w SAT Math, ACT Mathematics, A-levels Mathematics, IB Mathematics oraz egzaminach wstępnych na MIT, Harvard, Oxford. Prowadzimy zajęcia w języku angielskim za 300 zł miesięcznie."
  },
  {
    id: 5,
    category: "Założyciele",
    question: "Kto prowadzi Sigma Study?",
    answer: "Szkołę założyli dwaj pasjonaci matematyki: Łukasz oraz Kornel. Więcej informacji o nas znajdziesz na stronie głównej."
  },
  {
    id: 6,
    category: "Formy Zajęć",
    question: "W jakiej formie odbywają się zajęcia?",
    answer: "Zajęcia odbywają się głównie online w systemie 4-8 lekcji po 45-90 minut miesięcznie (zazwyczaj 60 minut). W przypadku potrzeb indywidualnych zapraszamy do kontaktu."
  },
  {
    id: 7,
    category: "Rozpoczęcie Nauki",
    question: "Jak mogę zapisać się na zajęcia?",
    answer: "Wypełnij formularz kontaktowy poniżej lub zadzwoń pod numer +48 532 533 220. Skontaktujemy się z Tobą w ciągu 24 godzin, żeby omówić szczegóły i zaplanować zajęcia."
  },
  {
    id: 8,
    category: "Efekty Nauki",
    question: "Jakie efekty mogę się spodziewać po kursie olimpiad matematycznych?",
    answer: "Nasz kurs Konkursy i Olimpiady to intensywny program 6 lekcji po 90 minut miesięcznie. Uczniowie rozwijają umiejętności rozwiązywania trudnych problemów matematycznych i zdobywają wiedzę potrzebną do startowania w konkursach."
  }
];

const FAQPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (id: number) => {
    setOpenItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="faq-page">
      <Helmet>
        <title>FAQ - Najczęściej Zadawane Pytania | Sigma Study</title>
        <meta name="description" content="Odpowiedzi na najczęściej zadawane pytania o korepetycje z matematyki w Sigma Study. Ceny, formy zajęć, kursy, efekty nauki." />
        <link rel="canonical" href="https://sigmastudy.pl/faq" />
        <meta property="og:title" content="FAQ - Najczęściej Zadawane Pytania | Sigma Study" />
        <meta property="og:description" content="Odpowiedzi na najczęściej zadawane pytania o korepetycje z matematyki w Sigma Study." />
        <meta property="og:url" content="https://sigmastudy.pl/faq" />
        <meta name="twitter:title" content="FAQ - Najczęściej Zadawane Pytania | Sigma Study" />
        <meta name="twitter:description" content="Odpowiedzi na najczęściej zadawane pytania o korepetycje z matematyki w Sigma Study." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* FAQ Content */}
      <section className="faq-content-section">
        <div className="faq-container">
          <div className="faq-header">
            <span className="faq-badge">FAQ</span>
            <h1 className="faq-title">Najczęściej zadawane pytania</h1>
            <p className="faq-subtitle">Znajdź odpowiedzi na najważniejsze pytania</p>
          </div>

          {/* FAQ Items */}
          <div className="faq-items">
            {faqData.map((item) => (
              <div key={item.id} className={`faq-item ${openItems.includes(item.id) ? 'open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => toggleItem(item.id)}
                  aria-expanded={openItems.includes(item.id)}
                >
                  <div className="question-content">
                    <span className="question-category">{item.category}</span>
                    <span className="question-text">{item.question}</span>
                  </div>
                  <span className="faq-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {openItems.includes(item.id) ? (
                        <path d="M18 15l-6-6-6 6" />
                      ) : (
                        <path d="M6 9l6 6 6-6" />
                      )}
                    </svg>
                  </span>
                </button>

                <div className={`faq-answer ${openItems.includes(item.id) ? 'open' : ''}`}>
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="faq-contact-section">
        <ContactForm />
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img
                  src="/logo_sigma/SVG/white.svg"
                  alt="Sigma Study School Logo"
                  className="footer-logo-img"
                />
              </div>
              <p>Profesjonalna szkoła matematyki</p>
              <p>Pomagamy osiągnąć sukces w matematyce od 2010 roku</p>
            </div>

            <div className="footer-section no-hover">
              <h4>Zajęcia</h4>
              <ul>
                <li>Szkoła Podstawowa</li>
                <li>Matematyka Liceum</li>
                <li>Przygotowanie do Matury</li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Kontakt</h4>
              <p>+48 532 533 220</p>
              <p>info@sigma-study.net</p>
              <p>ul. Matematyczna 15, Warszawa</p>
            </div>

            <div className="footer-section">
              <h4>Nasze social media</h4>
              <div className="social-links">
                <div style={{display: 'flex', gap: '12px'}}>
                  <SocialIcon url="https://www.facebook.com/people/Sigma-Study/61576967721817/"
                              style={{height: 30, width: 30}}/>
                  <SocialIcon url="https://www.instagram.com/sigma_study_/" style={{height: 30, width: 30}}/>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 Wszystkie prawa zastrzeżone.</p>
            <div className="footer-links">
              <Link to="/faq">FAQ</Link>
              <a href="/polityka-prywatnosci">Polityka prywatności</a>
              <a href="/regulamin">Regulamin</a>
              <a href="/cookies">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
